import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import Measure from "./Measure";
import Note from "./Note";
import LaneLine from "./LaneLine";

// --- Configuration ---
const MIN_LANE_WIDTH = 40; 
const LANE_COUNT = 5;     
const SIDE_MARGIN = 6; 
const DENSITY_BAR_WIDTH = 6; 
const DENSITY_BAR_GAP = 2;   
const MIN_NOTE_HEIGHT = 2;  

const FIXED_COL_WIDTH_OVERHEAD = SIDE_MARGIN + DENSITY_BAR_WIDTH + DENSITY_BAR_GAP + SIDE_MARGIN;
const MIN_COLUMN_WIDTH = FIXED_COL_WIDTH_OVERHEAD + (LANE_COUNT * MIN_LANE_WIDTH);
const CONTENT_OFFSET_X = SIDE_MARGIN + DENSITY_BAR_WIDTH + DENSITY_BAR_GAP;


// --- Density Bar Component (Local) ---
function DensityBar({ startTime, endTime, scale, data, maxDensity, densityWindow }: any) {
    const width = DENSITY_BAR_WIDTH;
    const step = 0.5;
    const rects = [];
    const totalDuration = endTime - startTime;
    const numSteps = Math.ceil(totalDuration / step);

    for (let i = 0; i < numSteps; i++) {
        const t = startTime + (i * step);
        const count = data.filter((n: any) => n.JudgeTime > t - densityWindow && n.JudgeTime <= t).length;
        const ratio = Math.min(1, count / maxDensity);
        const hue = 120 * (1 - ratio); 
        const color = `hsl(${hue}, 80%, 50%)`;

        const yBottom = (endTime - t) * scale;
        const nextT = Math.min(endTime, t + step);
        const yTop = (endTime - nextT) * scale;
        const height = Math.abs(yBottom - yTop);

        rects.push(
            <rect key={t} x={SIDE_MARGIN} y={yTop} width={width} height={height + 0.5} fill={color} shapeRendering="crispEdges" />
        );
    }
    return <g><rect x={SIDE_MARGIN} y={0} width={width} height={(endTime - startTime) * scale} fill="#e5e7eb" />{rects}</g>;
}

// --- Memoized Static Layer ---
const NotesLayer = React.memo((props: any) => {
    const { col, containerHeight, layoutMetrics, loadedChart, loadedMeasure, maxDensity, densityWindow, colors, onHover, onLeave } = props;
    
    return (
        <>
            <DensityBar startTime={col.start} endTime={col.end} scale={col.scale} data={loadedChart} maxDensity={maxDensity} densityWindow={densityWindow} />
            <LaneLine height={containerHeight} laneWidth={layoutMetrics.laneWidth} contentOffsetX={layoutMetrics.contentOffsetX} />
            
            {/* Measures */}
            {loadedMeasure.filter((t: number) => t >= col.start && t < col.end).map((m: number) => (
                <Measure key={m} time={m} measureCount={loadedMeasure.indexOf(m)+1} columnEndTime={col.end} scale={col.scale} laneWidth={layoutMetrics.laneWidth} contentOffsetX={layoutMetrics.contentOffsetX}/>
            ))}

            {/* Notes */}
            {loadedChart.filter((n: any) => n.JudgeTime >= col.start && n.JudgeTime < col.end).map((n: any, i: number) => (
                <Note key={i} noteProp={n} columnEndTime={col.end} scale={col.scale} laneWidth={layoutMetrics.laneWidth} contentOffsetX={layoutMetrics.contentOffsetX} count={loadedChart.indexOf(n)} color={colors[n.CastIndex]} onHover={onHover} onLeave={onLeave}/>
            ))}
        </>
    );
});


// --- Main ChartDetail Component ---
function ChartDetail(props: any) {
    const [loadedChart, setLoadedChart] = useState<any[]>([]);
    const [loadedMeasure, setLoadedMeasure] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(600);
    const [columns, setColumns] = useState<any[]>([]);
    const [hoverTooltip, setHoverTooltip] = useState<{x: number, y: number, content: any} | null>(null);
    
    const [layoutMetrics, setLayoutMetrics] = useState({
        colWidth: MIN_COLUMN_WIDTH,
        laneWidth: MIN_LANE_WIDTH,
        contentOffsetX: CONTENT_OFFSET_X
    });

    // Data Fetching
    useEffect(() => {
        setIsLoading(true);
        const baseId = props.id.split('?')[0]; 
        
        Promise.all([
            fetch(`${process.env.PUBLIC_URL}/charts/${baseId.toLowerCase()}.json`).then(r => r.json()),
            fetch(`${process.env.PUBLIC_URL}/charts/${baseId.toLowerCase()}_measures.json`).then(r => r.json())
        ]).then(([chartData, measureData]) => {
            setLoadedChart(chartData);
            setLoadedMeasure(measureData);
            setIsLoading(false);
        }).catch(err => {
            console.error("Error loading chart data", err);
            setIsLoading(false);
        });
    }, [props.id]);

    // Max Density Calculation
    const maxDensity = useMemo(() => {
        if (loadedChart.length === 0) return 1;
        let max = 0;
        const lastTime = loadedChart[loadedChart.length - 1].JudgeTime;
        for (let t = 0; t <= lastTime; t += 0.5) {
            const count = loadedChart.filter((n: any) => n.JudgeTime > t - props.densityWindow && n.JudgeTime <= t).length;
            if (count > max) max = count;
        }
        return Math.max(1, max);
    }, [loadedChart, props.densityWindow]);

    // Layout Calculation
    useLayoutEffect(() => {
        const calculateLayout = () => {
            if (containerRef.current && loadedMeasure.length > 0) {
                const availableHeight = containerRef.current.clientHeight; 
                // Use a large fit width to trigger scroll if needed, or stick to availableWidth but allow overflow
                // To force scroll when too many columns, we rely on the dynamic calculation.
                // Here we use availableWidth but enforce min column widths
                const availableWidth = containerRef.current.clientWidth;
                
                // We want to fit at least a few columns if possible, but if available width is huge, we use it.
                // If the user wants it scrollable, we shouldn't force fit everything into available width if it makes columns too small.
                // However, our logic calculates `dynamicColWidth` based on `fitWidth`.
                // Let's ensure fitWidth allows for scrolling if the calculated layout demands it.
                
                const fitWidth = Math.max(100, availableWidth - 20); 
                const fitHeight = Math.max(400, availableHeight);

                setContainerHeight(fitHeight);

                // Horizontal
                // Use availableWidth to determine how many columns CAN fit, 
                // but if we want horizontal scroll, we should perhaps be less aggressive about squeezing them?
                // The current logic tries to fit columns into the screen width. 
                // If we want horizontal scroll, we might want fixed column widths?
                // The prompt asked "when chart width is larger than screen, it will be cropped... make it scrollable".
                // This implies the previous logic was calculating a total width > screen width and getting cropped.
                // The previous logic was:
                // const maxCols = Math.max(1, Math.floor(fitWidth / MIN_COLUMN_WIDTH));
                // This constrains the number of columns to the visible width. 
                // If the user wants scrolling, they might mean they want to see MORE columns than fit?
                // OR, they simply mean the flex container was cropping.
                // Given the `overflow-x-auto` change, the existing logic will now scroll if `maxCols * dynamicColWidth` exceeds container,
                // but `dynamicColWidth` is calculated FROM `fitWidth`, so it usually fits exactly.
                
                // However, the `overflow-x-auto` on the parent is the key fix requested for the "cropping" behavior 
                // if the previous logic was somehow producing wider content (e.g. if `fitWidth` was manually set larger or calculation was off).
                // I will keep the logic as is, but ensure the container permits scrolling.

                const maxCols = Math.max(1, Math.floor(fitWidth / MIN_COLUMN_WIDTH));
                const dynamicColWidth = Math.floor(fitWidth / maxCols);
                const dynamicLaneWidth = (dynamicColWidth - FIXED_COL_WIDTH_OVERHEAD) / LANE_COUNT;
                
                // Vertical Scale
                const lastMeasureTime = loadedMeasure[loadedMeasure.length - 1];
                const totalDuration = lastMeasureTime;
                const targetDurationPerCol = totalDuration / maxCols;
                const baseScale = fitHeight / targetDurationPerCol;
                const adjustedScale = baseScale * 0.9; 

                setLayoutMetrics({
                    colWidth: dynamicColWidth,
                    laneWidth: dynamicLaneWidth,
                    contentOffsetX: CONTENT_OFFSET_X
                });

                // Column Splitting
                const newColumns = [];
                let chunkStartTime = 0;
                let measureIndex = 0;

                while (chunkStartTime < lastMeasureTime) {
                    let bestEndTime = chunkStartTime;
                    
                    // Find split point
                    for (let i = measureIndex; i < loadedMeasure.length; i++) {
                        const mTime = loadedMeasure[i];
                        const heightNeeded = (mTime - chunkStartTime) * adjustedScale;
                        if (heightNeeded <= fitHeight) {
                            bestEndTime = mTime;
                            measureIndex = i; 
                        } else {
                            break; 
                        }
                    }

                    if (bestEndTime <= chunkStartTime) {
                         if(measureIndex < loadedMeasure.length - 1) {
                             measureIndex++;
                             bestEndTime = loadedMeasure[measureIndex];
                         } else {
                             bestEndTime = lastMeasureTime;
                             if (bestEndTime <= chunkStartTime) break; 
                         }
                    }

                    newColumns.push({ start: chunkStartTime, end: bestEndTime, scale: adjustedScale });
                    chunkStartTime = bestEndTime;
                    if (chunkStartTime >= lastMeasureTime) break;
                }
                setColumns(newColumns);
            }
        };

        window.addEventListener('resize', calculateLayout);
        if (!isLoading) calculateLayout();
        return () => window.removeEventListener('resize', calculateLayout);
    }, [isLoading, loadedMeasure, loadedChart]);

    // Tooltip - Memoized with dependencies restored for detailed content
    const handleNoteHover = useCallback((e: React.MouseEvent, note: any) => {
        const t = note.JudgeTime;
        const count = loadedChart.filter((n: any) => n.JudgeTime > t - props.densityWindow && n.JudgeTime <= t).length;
        const index = loadedChart.indexOf(note);
        
        const content = (
            <div>
                <div className="font-bold">{index} / {note.JudgeTime.toFixed(2)}s</div>
                <div className="text-[10px] text-gray-300 mt-1">{count} notes / prev {props.densityWindow}s</div>
            </div>
        );
        setHoverTooltip({ x: e.clientX, y: e.clientY, content });
    }, [loadedChart, props.densityWindow]); 

    const handleLeave = useCallback(() => setHoverTooltip(null), []);

    if (isLoading) return <div className="flex w-full justify-center my-10">Loading Chart...</div>;

    return (
        <div ref={containerRef} className='flex flex-row overflow-x-auto border-t border-b border-gray-200 w-full h-full'>
            <div className="flex mx-auto" style={{ height: containerHeight }}>
                {columns.map((col, index) => (
                    <div key={index} className="relative border-r border-gray-300 bg-white" style={{ height: containerHeight, width: layoutMetrics.colWidth, flexShrink: 0 }}>
                        <div className="absolute top-0 right-1 text-[9px] text-gray-300 font-mono">{col.end.toFixed(1)}s</div>
                        <svg width="100%" height={containerHeight} className="block">
                            
                            {/* Render Static Layer (Memoized) */}
                            <NotesLayer 
                                col={col}
                                containerHeight={containerHeight}
                                layoutMetrics={layoutMetrics}
                                loadedChart={loadedChart}
                                loadedMeasure={loadedMeasure}
                                maxDensity={maxDensity}
                                densityWindow={props.densityWindow}
                                colors={props.colors}
                                onHover={handleNoteHover}
                                onLeave={handleLeave}
                            />

                            {/* Render Dynamic Playback Line (Frequent Updates) */}
                            {props.currentTime >= col.start && props.currentTime < col.end && (
                                <line 
                                    x1={layoutMetrics.contentOffsetX} 
                                    x2={layoutMetrics.contentOffsetX + (layoutMetrics.laneWidth * LANE_COUNT)}
                                    y1={(col.end - props.currentTime) * col.scale}
                                    y2={(col.end - props.currentTime) * col.scale}
                                    stroke="#ef4444" 
                                    strokeWidth="3"
                                    // Removed CSS transition for instant rAF tracking
                                />
                            )}
                        </svg>
                        <div className="absolute bottom-0 right-1 text-[9px] text-gray-300 font-mono">{col.start.toFixed(1)}s</div>
                    </div>
                ))}
            </div>
            {hoverTooltip && (
                <div style={{ position: 'fixed', top: hoverTooltip.y - 50, left: hoverTooltip.x + 10, backgroundColor: 'rgba(0,0,0,0.85)', color: 'white', padding: '6px', borderRadius: '4px', fontSize: '12px', zIndex: 9999, pointerEvents: 'none' }}>
                    {hoverTooltip.content}
                </div>
            )}
        </div>
    );
}

export default ChartDetail;